import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import {
  UserPlus, Pencil, Trash2, X, BarChart3,
  Crown, Award, DollarSign, Trophy, Volleyball,
  Star, User as UserIcon, Shield, Check, Users as UsersIcon
} from "lucide-react";
import { Panel } from "../components/primitives";
import { useToast } from "../components/Toast";
import { useActiveTeam } from "../components/ActiveTeam";
import { useVersion } from "../components/Version";
import PlayerProfile from "../components/PlayerProfile";
import MembersMobile from "./MembersMobile";
import MobileFormPage from "../components/MobileFormPage";
import { api } from "../api";
import { canDo } from "../data";

// MEMBER was removed everywhere in favor of PLAYER as the default catch-all.
const ALL_ROLES = [
  "PRESIDENT","VICE_PRESIDENT","FINANCE_HEAD","CRICKET_HEAD",
  "VOLLEYBALL_HEAD","CAPTAIN","PLAYER","ADMIN"
];

// Display metadata per role — label, icon, and a distinctive accent colour used
// by the fancy role-picker chips.
const ROLE_META = {
  PRESIDENT:       { label: "President",       Icon: Crown,       color: "#f7c948" },
  VICE_PRESIDENT:  { label: "Vice President",  Icon: Award,       color: "#b07cff" },
  FINANCE_HEAD:    { label: "Finance Head",    Icon: DollarSign,  color: "#43d893" },
  CRICKET_HEAD:    { label: "Cricket Head",    Icon: Trophy,      color: "#ff8a2a" },
  VOLLEYBALL_HEAD: { label: "Volleyball Head", Icon: Volleyball,  color: "#3ed3e0" },
  CAPTAIN:         { label: "Captain",         Icon: Star,        color: "#ffd24a" },
  PLAYER:          { label: "Player",          Icon: UserIcon,    color: "#5ea8ff" },
  ADMIN:           { label: "Admin",           Icon: Shield,      color: "#ff6b6b" }
};
function roleLabel(role) { return ROLE_META[role]?.label || role.replace("_", " "); }

// Strip any legacy MEMBER assignment from a role list; if that leaves nothing,
// fall back to PLAYER so the user always has a meaningful role.
function normalizeRoles(roles) {
  const filtered = (roles || []).filter(r => r !== "MEMBER");
  return filtered.length ? filtered : ["PLAYER"];
}

const EMPTY_FORM = {
  firstName: "", lastName: "", email: "", phone: "",
  password: "gabruz123", roles: ["PLAYER"], teamIds: []
};

export default function Members({ state, reload, user }) {
  const { version } = useVersion();
  const toast = useToast();
  const { activeTeam, activeTeamId, isAllTeams } = useActiveTeam();
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [ok, setOk] = useState("");

  // Esc closes the desktop modal. Listener only attaches while it's
  // open so it can't intercept Esc elsewhere on the page.
  useEffect(() => {
    if (!showForm) return;
    const onKey = e => { if (e.key === "Escape") close(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showForm]);
  // Phase 3 — cricket player profile modal. profileFor holds the player
  // + cricket team context, or null when closed.
  const [profileFor, setProfileFor] = useState(null);

  const canCreate = canDo(user, "members", "create");
  const canUpdate = canDo(user, "members", "update");
  const canDelete = canDo(user, "members", "delete");

  // Phase 3 — scope to active team unless leadership has picked "All Teams".
  const sortedMembers = useMemo(() => {
    let pool = state.members;
    if (!isAllTeams) {
      const ids = new Set(activeTeam?.players || []);
      pool = pool.filter(m => ids.has(m.id));
    }
    return [...pool].sort((a, b) =>
      (a.name || "").localeCompare(b.name || "", undefined, { sensitivity: "base" })
    );
  }, [state.members, activeTeam, isAllTeams]);

  function update(field, value) {
    setForm(prev => ({ ...prev, [field]: value }));
    setError(""); setOk("");
  }

  function toggleInList(field, value) {
    setForm(prev => {
      const list = prev[field];
      return {
        ...prev,
        [field]: list.includes(value) ? list.filter(x => x !== value) : [...list, value]
      };
    });
  }

  function openCreate() {
    setEditingId(null);
    // Pre-select the active team so new members default onto whichever team
    // the page is currently scoped to.
    setForm({ ...EMPTY_FORM, teamIds: activeTeam ? [activeTeam.id] : [] });
    setShowForm(true);
    setError(""); setOk("");
  }

  function openEdit(m) {
    setEditingId(m.id);
    const memberTeams = state.teams.filter(t => t.players?.includes(m.id)).map(t => t.id);
    setForm({
      firstName: m.firstName || "",
      lastName:  m.lastName  || "",
      email:     m.email     || "",
      phone:     m.phone     || "",
      password:  "",   // not edited from this UI
      roles:     normalizeRoles(m.roles),
      teamIds:   memberTeams
    });
    setShowForm(true);
    setError(""); setOk("");
  }

  function close() {
    setShowForm(false); setEditingId(null); setForm(EMPTY_FORM); setError(""); setOk("");
  }

  async function submit(e) {
    e.preventDefault();
    setError(""); setOk("");

    if (!form.email.trim() || !form.firstName.trim() || !form.phone.trim()) {
      setError("First name, email, and phone are required.");
      return;
    }
    if (form.roles.length === 0) {
      setError("Pick at least one role.");
      return;
    }

    setBusy(true);
    try {
      const fullName = `${form.firstName.trim()} ${form.lastName.trim()}`.trim();
      let userId;

      if (editingId) {
        // Update the existing member
        await api.updateMember(editingId, {
          firstName: form.firstName.trim(),
          lastName:  form.lastName.trim() || null,
          name:      fullName,
          phone:     form.phone.trim(),
          roles:     form.roles
        });
        userId = editingId;
      } else {
        const r = await api.createMember({
          email:     form.email.trim(),
          password:  form.password || "gabruz123",
          firstName: form.firstName.trim(),
          lastName:  form.lastName.trim() || null,
          name:      fullName,
          phone:     form.phone.trim(),
          roles:     form.roles
        });
        userId = r.id;
      }

      // Reconcile team membership — diff against current memberships.
      const currentMemberships = state.teams.filter(t => t.players?.includes(userId)).map(t => t.id);
      const toAdd    = form.teamIds.filter(tid => !currentMemberships.includes(tid));
      const toRemove = currentMemberships.filter(tid => !form.teamIds.includes(tid));

      for (const teamId of toAdd) {
        const team = state.teams.find(t => t.id === teamId);
        const players = [...(team?.players || []), userId];
        await api.updateTeam(teamId, { players });
      }
      for (const teamId of toRemove) {
        const team = state.teams.find(t => t.id === teamId);
        const players = (team?.players || []).filter(id => id !== userId);
        await api.updateTeam(teamId, { players });
      }

      setOk(editingId ? `Updated ${fullName}.` : `Added ${fullName}.`);
      close();
      await reload();
    } catch (err) {
      setError(err.message || "Failed to save member.");
    } finally {
      setBusy(false);
    }
  }

  async function remove(m) {
    if (!confirm(`Deactivate member "${m.name}"? This soft-deletes them — they can be reactivated from the database.`)) return;
    try {
      await api.deleteMember(m.id);
      await reload();
    } catch (err) {
      toast.error("Delete failed: " + err.message);
    }
  }

  // Shared form fields — used by both desktop inline Panel and V4 MobileFormPage.
  const renderFormFields = () => (
    <>
      <div className="two">
        <div>
          <label>First Name *</label>
          <input value={form.firstName} onChange={e => update("firstName", e.target.value)} placeholder="Ashish" />
        </div>
        <div>
          <label>Last Name</label>
          <input value={form.lastName} onChange={e => update("lastName", e.target.value)} placeholder="Bhat" />
        </div>
      </div>
      <div className="two">
        <div>
          <label>Email *</label>
          <input type="email" value={form.email} onChange={e => update("email", e.target.value)} placeholder="name@gabruzuniverse.com" disabled={!!editingId} />
        </div>
        <div>
          <label>Phone *</label>
          <input value={form.phone} onChange={e => update("phone", e.target.value)} placeholder="412-555-0000" />
        </div>
      </div>
      {!editingId && (
        <div>
          <label>Initial Password</label>
          <input value={form.password} onChange={e => update("password", e.target.value)} placeholder="gabruz123" />
          <small style={{ display: "block", color: "var(--muted)", marginTop: 4 }}>
            Defaults to <code>gabruz123</code> if left blank. User can change it after first login.
          </small>
        </div>
      )}
      <div>
        <label className="mfp-sectionLabel">
          <span>Roles *</span>
          <em>{form.roles.length} selected</em>
        </label>
        <div className="rolePicker">
          {ALL_ROLES.map(role => {
            const meta = ROLE_META[role];
            const Icon = meta.Icon;
            const on = form.roles.includes(role);
            return (
              <button
                key={role}
                type="button"
                className={`roleChip${on ? " roleChip--on" : ""}`}
                style={{
                  "--role-color": meta.color,
                  "--role-color-glow": `${meta.color}33`
                }}
                onClick={() => toggleInList("roles", role)}
              >
                <span className="roleChip-iconWrap"><Icon size={16}/></span>
                <span className="roleChip-name">{meta.label}</span>
                {on && <span className="roleChip-check"><Check size={13} strokeWidth={3}/></span>}
              </button>
            );
          })}
        </div>
      </div>

      <div>
        <label className="mfp-sectionLabel">
          <span>Team Assignment</span>
          <em>{form.teamIds.length} selected</em>
        </label>
        <div className="teamPicker">
          {state.teams.length === 0 && (
            <div className="teamPicker-empty">
              <UsersIcon size={18}/> No teams exist yet.
            </div>
          )}
          {state.teams.map(t => {
            const on = form.teamIds.includes(t.id);
            const isCricket = t.sport === "Cricket";
            return (
              <button
                key={t.id}
                type="button"
                className={`teamCard teamCard--${isCricket ? "cricket" : "volleyball"}${on ? " teamCard--on" : ""}`}
                onClick={() => toggleInList("teamIds", t.id)}
              >
                <span className="teamCard-iconWrap">
                  {isCricket ? <Trophy size={18}/> : <Volleyball size={18}/>}
                </span>
                <span className="teamCard-body">
                  <span className="teamCard-name">{t.name}</span>
                  <span className="teamCard-meta">
                    {t.sport} · {t.players?.length || 0} player{(t.players?.length || 0) === 1 ? "" : "s"}
                  </span>
                </span>
                <span className="teamCard-check" aria-hidden={!on}>
                  {on ? <Check size={14} strokeWidth={3}/> : null}
                </span>
              </button>
            );
          })}
        </div>
        <small className="mfp-fieldHint">
          Pick the teams this member should belong to. Edits add/remove them on save.
        </small>
      </div>
    </>
  );

  // Desktop form — portalled modal popup so it opens over the members
  // list instead of pushing it down. Scrim click + top-right X + Esc
  // key all close it. Wrapped in createPortal so it lives at the root
  // stacking context (avoids z-index fights with the sticky topbar).
  const formModal = showForm && (canCreate || canUpdate) && createPortal((
    <div className="modal" onClick={close}>
      <div
        className="modalInner mem-modal-inner"
        onClick={e => e.stopPropagation()}
      >
        <button
          type="button"
          className="close"
          onClick={close}
          aria-label="Close"
        ><X size={20}/></button>
        <Panel title={editingId ? "Edit Member" : "Add New Member"}>
          <form className="form" onSubmit={submit}>
            {renderFormFields()}
            {error && <div className="err">{error}</div>}
            {ok    && <div className="note">{ok}</div>}
            <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
              <button type="button" className="dark" onClick={close} disabled={busy}>Cancel</button>
              <button type="submit" className="btn" disabled={busy}>
                {busy ? "Saving…" : (editingId
                  ? <>Save Changes</>
                  : <><UserPlus size={16}/> Add Member</>)}
              </button>
            </div>
          </form>
        </Panel>
      </div>
    </div>
  ), document.body);

  // V4 — branch into the mobile card list. When the form is open,
  // it takes over the full screen via MobileFormPage instead of
  // crowding the top of the members list.
  if (version === "v4") {
    if (showForm && (canCreate || canUpdate)) {
      return (
        <MobileFormPage
          title={editingId ? "Edit Member" : "Add New Member"}
          onBack={close}
          footer={
            <>
              <button type="button" className="mfp-btn mfp-btn--ghost" onClick={close} disabled={busy}>Cancel</button>
              <button type="submit" form="membersMfpForm" className="mfp-btn mfp-btn--primary" disabled={busy}>
                {busy ? "Saving…" : (editingId ? "Save Changes" : "Add Member")}
              </button>
            </>
          }
        >
          <form id="membersMfpForm" className="form" onSubmit={submit}>
            {renderFormFields()}
            {error && <div className="err">{error}</div>}
            {ok    && <div className="note">{ok}</div>}
          </form>
        </MobileFormPage>
      );
    }
    return (
      <MembersMobile
        state={state}
        reload={reload}
        user={user}
        openCreate={openCreate}
        openEdit={openEdit}
      />
    );
  }

  return (
    <div className="stack">
      <div className="builderTop">
        <div>
          <p>Roster · Community · Leadership</p>
          <h2>Members &amp; Leadership</h2>
        </div>
        <div>
          {canCreate && (
            <button className="btn" onClick={openCreate}>
              <UserPlus size={16}/> Add Member
            </button>
          )}
        </div>
      </div>

      {formModal}

      <Panel title={isAllTeams ? "All Members" : `${activeTeam?.name || "Team"} Members`}>
        <table>
          <thead>
            <tr>
              <th>Name</th>
              <th>Email</th>
              <th>Phone</th>
              <th>Roles</th>
              <th>Teams</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {sortedMembers.map(u => {
              const teams = state.teams.filter(t => t.players?.includes(u.id));
              const roles = (u.roles || []).filter(r => r !== "MEMBER");
              // Phase 3 — cricket profile button only shows for players
              // who sit on at least one cricket team. If the active team
              // is itself a cricket team, prefer that as the profile
              // scope; otherwise fall back to their first cricket team.
              const cricketTeams = teams.filter(t => t.sport === "Cricket");
              const profileTeam = !isAllTeams && activeTeam?.sport === "Cricket"
                ? activeTeam
                : cricketTeams[0];
              return (
                <tr key={u.id}>
                  <td><b>{u.name}</b></td>
                  <td>{u.email}</td>
                  <td>{u.phone || "—"}</td>
                  <td>
                    {roles.length === 0
                      ? <span style={{color:"var(--muted)"}}>—</span>
                      : (
                        <div className="tagRow">
                          {roles.map(r => (
                            <span key={r} className={`roleTag roleTag--${r}`}>{roleLabel(r)}</span>
                          ))}
                        </div>
                      )}
                  </td>
                  <td>
                    {teams.length === 0
                      ? <span style={{color:"var(--muted)"}}>—</span>
                      : (
                        <div className="tagRow">
                          {teams.map(t => (
                            <span key={t.id} className={`teamTag teamTag--${t.sport}`}>{t.name}</span>
                          ))}
                        </div>
                      )}
                  </td>
                  <td>
                    <span className={`statusTag statusTag--${u.active ? "active" : "inactive"}`}>
                      {u.active ? "Active" : "Inactive"}
                    </span>
                  </td>
                  <td style={{ display:"flex", gap:6 }}>
                    {profileTeam && (
                      <button
                        className="dark"
                        onClick={() => setProfileFor({ user: u, team: profileTeam })}
                        title={`View cricket stats (${profileTeam.name})`}
                      >
                        <BarChart3 size={14}/>
                      </button>
                    )}
                    {canUpdate && (
                      <button className="dark" onClick={() => openEdit(u)} title="Edit">
                        <Pencil size={14}/>
                      </button>
                    )}
                    {canDelete && u.active && (
                      <button
                        className="dark"
                        onClick={() => remove(u)}
                        title="Deactivate"
                        style={{color:"var(--red)", borderColor:"rgba(255,107,107,0.4)"}}
                      >
                        <Trash2 size={14}/>
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </Panel>

      {profileFor && (
        <PlayerProfile
          user={profileFor.user}
          team={profileFor.team}
          currentUser={user}
          onClose={() => setProfileFor(null)}
        />
      )}
    </div>
  );
}
